import { Button, Flex, Group, Pagination } from '@mantine/core'
import { useDebouncedValue, useDisclosure, useInterval } from '@mantine/hooks'
import { IconPlus, IconRefresh, IconTerminal2 } from '@tabler/icons-react'
import { useCallback, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { DeploymentGroupDrawer } from '@/components/deployments/deployment-details/DeploymentGroupDrawer'
import { useDeploymentGroupDrawer } from '@/components/deployments/deployment-details/useDeploymentGroupDrawer'
import { FixedPageTitle } from '@/components/layout/PageWithFixedToolbar/FixedPageTitle'
import { PageWithFixedToolbar } from '@/components/layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { useTableState } from '@/components/ui-shared/Tables/useTableState'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDeploymentOverview } from '@/queries/deploymentQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { CreateDeploymentModal } from './DeploymentList/CreateDeploymentModal'
import { DeploymentList } from './DeploymentList/DeploymentList'
import { DeploymentListFilterConfig } from './DeploymentList/DeploymentListFilter/DeploymentListFilter'
import { AppDeploymentActions } from './DeploymentTree/AppDeploymentActions'
import { DeploymentGroupActions } from './DeploymentTree/DeploymentGroupActions'
import { GroupMemberActions } from './DeploymentTree/GroupMemberActions'

const PAGE_SIZE = 10
const POLL_INTERVAL = 15000

const DEFAULT_FILTER_CONFIG: DeploymentListFilterConfig = {
  application_id: '',
  device_id: '',
  stream_id: '',
  group_id: ''
}

export const DeploymentsPage = () => {
  const [canCreateDeployment] = useHasPermissions(Resource.Deployments, [
    CrudAction.Create
  ])

  const [
    isCreateDeploymentModalOpened,
    { open: openCreateDeploymentModal, close: closeCreateDeploymentModal }
  ] = useDisclosure(false)

  const [tableState, setTableState] = useTableState({
    activePage: 1,
    filterConfig: DEFAULT_FILTER_CONFIG
  })

  const { activePage, filterConfig } = tableState
  const [debouncedFilterConfig] = useDebouncedValue(filterConfig, 150)

  const { data, isLoading, isFetching, isError, refetch } =
    useGetDeploymentOverview(
      {
        offset: getPageOffset(activePage, PAGE_SIZE),
        limit: PAGE_SIZE,
        application_id: debouncedFilterConfig.application_id,
        device_id: debouncedFilterConfig.device_id,
        stream_id: debouncedFilterConfig.stream_id,
        group_id: debouncedFilterConfig.group_id
      },
      {
        keepPreviousData: true
      }
    )

  const apps = data?.results || []
  const groupsEnabled = data?.groups_enabled ?? false
  const totalPageCount = getPageCount(data?.count || 0, PAGE_SIZE)

  const {
    opened: isGroupDrawerOpen,
    selection: groupSelection,
    openGroup,
    close: closeGroupDrawer
  } = useDeploymentGroupDrawer(apps)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const { start, stop, active } = useInterval(
    () => void refetch(),
    POLL_INTERVAL
  )

  useEffect(() => {
    if (data && !active) {
      start()
    }
  }, [data, active, start])

  // Cleanup function to stop the interval when the component is unmounted
  useEffect(() => {
    return stop
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleReload = useCallback(() => {
    void refetch()
  }, [refetch])

  return (
    <PageWithFixedToolbar
      toolbar={
        <FixedPageTitle
          title={<FormattedMessage id="deployments.list.title" />}
          leftSection={
            <Group gap="xs" wrap="nowrap">
              {canCreateDeployment && (
                <Button
                  size="xs"
                  leftSection={<IconPlus size={16} />}
                  onClick={openCreateDeploymentModal}
                >
                  <FormattedMessage id="deployments.create" />
                </Button>
              )}

              <Button
                size="xs"
                variant="outline"
                leftSection={<IconRefresh size={16} />}
                loading={isFetching}
                onClick={() => void refetch()}
              >
                <FormattedMessage id="refresh" />
              </Button>
            </Group>
          }
        />
      }
    >
      <DeploymentList
        apps={apps}
        groupsEnabled={groupsEnabled}
        isLoading={isLoading}
        hasError={isError}
        filterConfig={filterConfig}
        renderAppActions={(app) => <AppDeploymentActions app={app} />}
        renderGroupActions={(group, context) => (
          <DeploymentGroupActions group={group} context={context} />
        )}
        renderMemberActions={(member, group, context) => (
          <Group gap="xs" wrap="nowrap">
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconTerminal2 size={14} />}
              onClick={() => openGroup(group, member, 'app-logs')}
            >
              <FormattedMessage id="deployments.groups.details.viewLogs" />
            </Button>
            <GroupMemberActions
              member={member}
              group={group}
              context={context}
            />
          </Group>
        )}
        onFilter={(filterConfig) =>
          setTableState({ activePage: 1, filterConfig })
        }
        onReload={handleReload}
        onOpenGroup={openGroup}
      />

      {groupSelection && (
        <DeploymentGroupDrawer
          opened={isGroupDrawerOpen}
          group={groupSelection.group}
          label={groupSelection.label}
          deviceId={groupSelection.overviewDevice.device_id}
          appId={groupSelection.app.application_id}
          appName={groupSelection.app.application_name}
          initialTab={groupSelection.tab}
          initialDeploymentId={groupSelection.deploymentId}
          onClose={closeGroupDrawer}
        />
      )}

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <CreateDeploymentModal
        opened={isCreateDeploymentModalOpened}
        onClose={closeCreateDeploymentModal}
      />
    </PageWithFixedToolbar>
  )
}
